import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useCouponByIdRelayQuery,
  mapCouponToICoupon,
} from '../../hooks/coupons/query/V2';
import AssignCouponToUser from '../../components/coupons/AssignCouponToUser';
import { ICoupon } from '../../ts/interface/coupon.interface';
import { connect } from 'react-redux';
import { findAccess, getLocaleDateTimeString, history } from '../../utils';
import {
  ValidDaysPills,
  ValidTimePill,
  ValidDatesPill,
} from '../../components/coupons/CouponRulePills';
import PageContainer from '../../components/Redesing/page-container';
import PageContent from '../../components/Redesing/page-content';
import { Stack } from '@mui/system';
import { PropertyList } from '../../components/Redesing/property-list';
import { PropertyListItem } from '../../components/Redesing/property-list-item';
import { Card as MuiCard } from '@mui/material';
import CardView from '../../components/Redesing/card-view';
import RentalsTableCard from '../../components/Redesing/table/rental/rentals-table-card';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import DetailPageSkeleton from '../../components/Redesing/skeleton/DetailPageSkeleton';

const CouponDetail = ({
  systemLoaded,
  systemAccess,
  movaticAccess,
}: {
  systemLoaded: boolean;
  systemAccess: number;
  movaticAccess: number;
}) => {
  const { couponId } = useParams<{ couponId: string }>();
  const { data: couponData, isLoading } = useCouponByIdRelayQuery(couponId, {
    enabled: systemLoaded,
  });
  const coupon = useMemo(
    () => (couponData ? mapCouponToICoupon(couponData) : ({} as ICoupon)),
    [couponData]
  );

  const isOrgScoped = coupon?.organizationId != null && coupon?.organizationId !== '';
  const canAssign = findAccess(systemAccess).includes('rates') || Boolean(movaticAccess);

  const [showAssignModal, setShowAssignModal] = useState(false);

  return (
    <>
      <PageContainer>
        {isLoading || !systemLoaded ? (
          <DetailPageSkeleton
            cards={[{ rows: 4, title: true }]}
            showTable
            tableRows={3}
          />
        ) : (
          <PageContent
            backLink={'/billing/coupons'}
            contentTitle={'Coupons'}
            itemName={coupon.name || coupon.code || ''}
            itemChipValue={coupon.gid || coupon.id || ''}
            chipLabel={'Coupon ID'}
            tabsChildren={
              <Stack spacing={3}>
                <CardView
                  title={'Coupon Details'}
                  content={
                    <PropertyList>
                      <PropertyListItem divider label="Scope">
                        <SeverityPill color={isOrgScoped ? 'warning' : 'info'}>
                          {isOrgScoped ? 'Organization' : 'System'}
                        </SeverityPill>
                      </PropertyListItem>
                      <PropertyListItem divider label="Code" value={coupon?.code} />
                      <PropertyListItem
                        divider
                        label="Discount"
                        value={coupon?.discount || ''}
                      />
                      <PropertyListItem
                        divider
                        label="Valid For"
                        value={
                          coupon?.duration
                            ? coupon.duration
                            : coupon?.uses
                            ? coupon.uses
                            : ''
                        }
                      />
                      <PropertyListItem divider label="Assignment only">
                        <SeverityPill color={coupon?.assignmentOnly ? 'warning' : 'info'}>
                          {coupon?.assignmentOnly ? 'Yes' : 'No'}
                        </SeverityPill>
                      </PropertyListItem>
                      <PropertyListItem divider label="Valid days">
                        <ValidDaysPills mask={coupon?.validDays} />
                      </PropertyListItem>
                      <PropertyListItem divider label="Valid time">
                        <ValidTimePill
                          start={coupon?.validTimeStart}
                          end={coupon?.validTimeEnd}
                        />
                      </PropertyListItem>
                      <PropertyListItem divider label="Valid dates">
                        <ValidDatesPill
                          from={coupon?.validFrom}
                          until={coupon?.validUntil}
                        />
                      </PropertyListItem>
                      <PropertyListItem
                        divider
                        label="Created"
                        value={
                          coupon?.created ? getLocaleDateTimeString(coupon.created) : '—'
                        }
                      />
                      <PropertyListItem label="Active">
                        <SeverityPill color={coupon?.active ? 'success' : 'error'}>
                          {coupon?.active ? 'Yes' : 'No'}
                        </SeverityPill>
                      </PropertyListItem>
                    </PropertyList>
                  }
                />
                <MuiCard>
                  <RentalsTableCard
                    noRentalsText={
                      'This coupon has not been used in any rentals matching the criteria'
                    }
                    parameters={{ coupon_id: couponData?.oldId || '' }}
                  />
                </MuiCard>
              </Stack>
            }
            itemTopButtons={
              <IconButtonMenu
                buttons={[
                  isOrgScoped
                    ? {
                        label: 'Managed at organization level',
                        onClick: () => {
                          history.push(`/organization/billing/coupon/${couponId}`);
                        },
                        startIcon: <EditIcon />,
                      }
                    : {
                        label: 'Edit',
                        onClick: () => {
                          history.push(`/billing/coupon/edit/${couponId}`);
                        },
                        startIcon: <EditIcon />,
                      },
                  {
                    label: 'Assign to user',
                    onClick: () => setShowAssignModal(true),
                    startIcon: <PersonAddIcon />,
                    disabled: !canAssign,
                  },
                  {
                    label: 'View assignments',
                    onClick: () => {
                      history.push(`/billing/coupon/${couponId}/assignments`);
                    },
                    startIcon: <GroupIcon />,
                  },
                ]}
              />
            }
          />
        )}
      </PageContainer>
      {showAssignModal && (
        <AssignCouponToUser
          scope="system"
          open={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => setShowAssignModal(false)}
          couponOldId={couponData?.oldId ?? ''}
          couponName={coupon.name || coupon.code || 'Coupon'}
          systemAccess={systemAccess}
          movaticAccess={movaticAccess}
        />
      )}
    </>
  );
};

export default connect(
  (state: {
    system: { isLoaded: boolean };
    admin: { admin: { admin: { movaticAccess: number } }; systemAccess: number };
  }) => ({
    systemLoaded: state.system.isLoaded,
    systemAccess: state.admin.systemAccess,
    movaticAccess: state.admin.admin.admin.movaticAccess,
  }),
  () => ({})
)(CouponDetail);
